import { useCallback, useEffect, useRef, useState } from "react";
import { Download, FileArchive, FileSpreadsheet, FileText, File as FileIcon, Star, Trash2, UploadCloud, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { api, apiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const B = process.env.REACT_APP_BACKEND_URL;
const ACCEPT = ".jpg,.jpeg,.png,.webp,.heic,.pdf,.xls,.xlsx,.doc,.docx,.csv";
const EXTS = ACCEPT.split(",").map((x) => x.slice(1));
const size = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const Icon = ({ a }) => {
  const C = ["xls", "xlsx", "csv"].includes(a.ext) ? FileSpreadsheet : ["pdf", "doc", "docx"].includes(a.ext) ? FileText : FileIcon;
  return <C className={`h-8 w-8 ${a.ext === "pdf" ? "text-rose-600" : ["xls", "xlsx", "csv"].includes(a.ext) ? "text-emerald-600" : "text-blue-600"}`} />;
};

function Lightbox({ a, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/80 grid place-items-center p-6" onClick={onClose} data-testid="attachment-lightbox">
      <button className="absolute top-4 right-4 text-white" onClick={onClose} data-testid="attachment-lightbox-close"><X className="h-6 w-6" /></button>
      <img src={B + a.view_url} alt={a.file_name} className="max-h-[85vh] max-w-[90vw] object-contain rounded" onClick={(e) => e.stopPropagation()} />
      <div className="absolute bottom-4 text-white text-sm">{a.file_name}{a.note ? ` · ${a.note}` : ""}</div>
    </div>
  );
}

function Tile({ a, canDelete, primaryMode, onOpen, onDelete, onPrimary, onNote }) {
  return (
    <div className={`border rounded-md overflow-hidden bg-white flex flex-col ${a.is_primary ? "ring-2 ring-amber-400" : ""}`} data-testid={`attachment-${a.id}`}>
      <button className="h-32 bg-slate-50 grid place-items-center" onClick={() => onOpen(a)} title={a.file_name} data-testid={`attachment-open-${a.id}`}>
        {a.thumb_url ? <img src={B + a.thumb_url} alt="" className="h-32 w-full object-cover" loading="lazy" /> : <Icon a={a} />}
      </button>
      <div className="p-2 text-[11px] space-y-0.5 flex-1">
        <div className="font-medium truncate" title={a.file_name}>{a.is_primary && <Star className="inline h-3 w-3 text-amber-500 mr-0.5" />}{a.file_name}</div>
        <div className="text-slate-500">{a.ext.toUpperCase()} · {size(a.size)}</div>
        <div className="text-slate-400 truncate">{a.uploaded_by_name} · {formatDate(a.uploaded_at)}</div>
        <button className="text-slate-500 hover:text-blue-700 truncate max-w-full text-left" onClick={() => onNote(a)} data-testid={`attachment-note-${a.id}`}>{a.note || "+ not"}</button>
      </div>
      <div className="flex border-t">
        <a className="flex-1 grid place-items-center py-1 hover:bg-slate-50" href={B + a.download_url} data-testid={`attachment-download-${a.id}`} title="İndir"><Download className="h-3.5 w-3.5" /></a>
        {primaryMode && a.is_image && !a.is_primary && <button className="flex-1 grid place-items-center py-1 hover:bg-amber-50" onClick={() => onPrimary(a)} title="Ana görsel yap" data-testid={`attachment-primary-${a.id}`}><Star className="h-3.5 w-3.5" /></button>}
        {canDelete && <button className="flex-1 grid place-items-center py-1 hover:bg-rose-50 text-rose-600" onClick={() => onDelete(a)} title="Sil" data-testid={`attachment-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
      </div>
    </div>
  );
}

export default function AttachmentsPanel({ entityType, entityId, primaryMode = false, onPrimaryChange, title = "Dosyalar" }) {
  const { can, user } = useAuth();
  const [d, setD] = useState(null);
  const [prog, setProg] = useState(null);
  const [errors, setErrors] = useState([]);
  const [note, setNote] = useState("");
  const [drag, setDrag] = useState(false);
  const [box, setBox] = useState(null);
  const inp = useRef(null);
  const load = useCallback(() => api.get("/attachments", { params: { entity_type: entityType, entity_id: entityId } }).then(({ data }) => setD(data)).catch((e) => setD({ error: apiError(e) })), [entityType, entityId]);
  useEffect(() => { load(); }, [load]);
  const upload = async (list) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    const lim = d?.limits || { max_mb: 25 };
    const pre = files.filter((f) => !EXTS.includes(f.name.split(".").pop().toLowerCase()) || f.size > lim.max_mb * 1048576)
      .map((f) => ({ file_name: f.name, error: f.size > lim.max_mb * 1048576 ? `En fazla ${lim.max_mb} MB` : "Desteklenmeyen tür" }));
    const okf = files.filter((f) => !pre.some((p) => p.file_name === f.name));
    setErrors(pre);
    if (!okf.length) return;
    const fd = new FormData();
    fd.append("entity_type", entityType); fd.append("entity_id", entityId); fd.append("note", note);
    okf.forEach((f) => fd.append("files", f));
    setProg(0);
    try {
      const { data } = await api.post("/attachments", fd, { onUploadProgress: (e) => setProg(Math.round((e.loaded / (e.total || 1)) * 100)) });
      setErrors([...pre, ...data.results.filter((r) => !r.ok)]);
      if (data.uploaded) toast.success(`${data.uploaded} dosya yüklendi`);
      setNote(""); load();
    } catch (e) { toast.error(apiError(e)); }
    setProg(null);
  };
  const open = (a) => (a.is_image && a.view_url ? setBox(a) : a.view_url ? window.open(B + a.view_url, "_blank") : (window.location.href = B + a.download_url));
  const del = async (a) => {
    const reason = window.prompt(`"${a.file_name}" silinsin mi? Gerekçe (zorunlu):`);
    if (!reason || reason.trim().length < 3) { if (reason !== null) toast.error("Gerekçe en az 3 karakter olmalı"); return; }
    try { await api.delete(`/attachments/${a.id}`, { data: { reason: reason.trim() } }); toast.success("Dosya silindi"); load(); if (a.is_primary) onPrimaryChange?.(); } catch (e) { toast.error(apiError(e)); }
  };
  const primary = async (a) => { try { await api.post(`/attachments/${a.id}/primary`); toast.success("Ana görsel güncellendi"); load(); onPrimaryChange?.(); } catch (e) { toast.error(apiError(e)); } };
  const editNote = async (a) => {
    const n = window.prompt("Dosya notu / etiketi (ör. Sözleşme, Vergi levhası):", a.note || "");
    if (n === null) return;
    try { await api.patch(`/attachments/${a.id}`, { note: n.trim() || null }); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const zip = async () => {
    try {
      const { data, headers } = await api.get("/attachments/zip", { params: { entity_type: entityType, entity_id: entityId }, responseType: "blob" });
      const m = /filename\*=UTF-8''([^;]+)/.exec(headers["content-disposition"] || "");
      const el = document.createElement("a"); el.href = URL.createObjectURL(data); el.download = m ? decodeURIComponent(m[1]) : "dosyalar.zip"; el.click();
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!d) return <div className="h-24 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div>;
  if (d.error) return <div className="text-rose-600 text-sm p-3" data-testid="attachments-error">{d.error}</div>;
  const ro = user?.role_key === "inceleme";
  return (
    <div className="space-y-3" data-testid={`attachments-panel-${entityType}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[13px] font-semibold">{title} <span className="text-slate-400 font-normal">({d.items.length}/{d.limits.max_files} · dosya başına en fazla {d.limits.max_mb} MB)</span></div>
        <div className="ml-auto flex gap-2">{d.items.length > 0 && <Button size="sm" variant="outline" onClick={zip} data-testid="attachments-zip"><FileArchive className="h-4 w-4 mr-1" />Tümünü ZIP</Button>}</div>
      </div>
      {!ro && <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
        className={`border-2 border-dashed rounded-md p-4 flex flex-col md:flex-row items-center gap-3 ${drag ? "border-blue-500 bg-blue-50" : "border-slate-200"}`} data-testid="attachments-dropzone">
        <UploadCloud className="h-6 w-6 text-slate-400" />
        <div className="text-[12px] text-slate-500 flex-1">Dosyaları sürükleyip bırakın veya seçin · JPG, PNG, WEBP, HEIC, PDF, XLS(X), DOC(X), CSV</div>
        <Input className="h-8 w-56 text-[12px]" placeholder="Not (opsiyonel, tüm dosyalara)" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} data-testid="attachments-note" />
        <input ref={inp} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="attachments-input" />
        <Button size="sm" onClick={() => inp.current?.click()} disabled={prog !== null} data-testid="attachments-select">Dosya Seç</Button>
      </div>}
      {prog !== null && <div className="flex items-center gap-2 text-[12px]" data-testid="attachments-progress"><Progress value={prog} className="h-2 flex-1" />%{prog}</div>}
      {errors.length > 0 && <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 space-y-0.5" data-testid="attachments-errors">
        {errors.map((e, i) => <div key={i}><b>{e.file_name}</b>: {e.error}</div>)}</div>}
      {d.items.length === 0 ? <div className="text-[13px] text-slate-400 py-4 text-center" data-testid="attachments-empty">Henüz dosya yok</div>
        : <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3">{d.items.map((a) => <Tile key={a.id} a={a} canDelete={d.can_delete} primaryMode={primaryMode && can("urunler", "edit")}
          onOpen={open} onDelete={del} onPrimary={primary} onNote={editNote} />)}</div>}
      {box && <Lightbox a={box} onClose={() => setBox(null)} />}
    </div>
  );
}
